/**
 * Single source of truth for the locales the microsite ships
 * translations for. Each entry describes:
 *   - `code`        BCP-47 tag used by react-intl-next (`pt-BR`, not `pt_BR`)
 *   - `nativeName`  How the locale labels itself in the dropdown
 *   - `loadMessages` Lazy import of the Traduki bundle re-exported by
 *                    this package's `./i18n/<code>` subpath
 *
 * The keys of the dynamic-import paths use the underscore form because
 * that's how Traduki names the files (see ./src/i18n/*.ts).
 *
 * IMPORTANT: When a new locale is added under `src/i18n/`, append it
 * here. Nothing else needs to change.
 */

export type LocaleCode =
	| 'en'
	| 'en-GB'
	| 'en-ZZ'
	| 'de'
	| 'pt-BR'
	| 'sl'
	| 'sr-RS'
	| 'tr'
	| 'zh-TW';

export type LocaleMessages = Record<string, string>;

export type LocaleDescriptor = {
	code: LocaleCode;
	nativeName: string;
	loadMessages: () => Promise<LocaleMessages>;
};

const importDefault = async <T>(p: Promise<{ default: T }>): Promise<T> => (await p).default;

export const SUPPORTED_LOCALES: readonly LocaleDescriptor[] = [
	{
		code: 'en',
		nativeName: 'English',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-en" */ '../../i18n/en')),
	},
	{
		code: 'en-GB',
		nativeName: 'English (UK)',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-en_GB" */ '../../i18n/en_GB')),
	},
	{
		// Pseudo-locale, useful for QA. Hidden from the dropdown via
		// `SELECTABLE_LOCALES` below but still loadable if persisted.
		code: 'en-ZZ',
		nativeName: 'English (pseudo)',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-en_ZZ" */ '../../i18n/en_ZZ')),
	},
	{
		code: 'de',
		nativeName: 'Deutsch',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-de" */ '../../i18n/de')),
	},
	{
		code: 'pt-BR',
		nativeName: 'Português (Brasil)',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-pt_BR" */ '../../i18n/pt_BR')),
	},
	{
		code: 'sl',
		nativeName: 'Slovenščina',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-sl" */ '../../i18n/sl')),
	},
	{
		code: 'sr-RS',
		nativeName: 'Српски',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-sr_RS" */ '../../i18n/sr_RS')),
	},
	{
		code: 'tr',
		nativeName: 'Türkçe',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-tr" */ '../../i18n/tr')),
	},
	{
		code: 'zh-TW',
		nativeName: '繁體中文',
		loadMessages: () =>
			importDefault(import(/* webpackChunkName: "twg-microsite-i18n-zh_TW" */ '../../i18n/zh_TW')),
	},
] as const;

/** Locales offered in the footer dropdown (excludes pseudo-locale). */
export const SELECTABLE_LOCALES: readonly LocaleDescriptor[] = SUPPORTED_LOCALES.filter(
	(l) => l.code !== 'en-ZZ',
);

export const DEFAULT_LOCALE: LocaleCode = 'en';

export const isSupportedLocale = (value: string | null | undefined): value is LocaleCode =>
	value !== null && value !== undefined && SUPPORTED_LOCALES.some((l) => l.code === value);

export const getDescriptor = (code: LocaleCode): LocaleDescriptor =>
	// Safe non-null assert: type guard ensures `code` is in the catalog.
	SUPPORTED_LOCALES.find((l) => l.code === code) as LocaleDescriptor;
